import React from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs';
import { TextArea } from '@/components/ui/inputs/TextArea';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Text } from '@/components/ui/text/Text';
import { InfoBubble } from '@/components/ui/tooltips/InfoBubble';
import { SidePanelFormLayout, PermissionGuard } from '@/components/layout';
import { IconScout } from '@/components/ui/images/IconScout';
import { DecimalConversionIcon } from '@/components/ui/images/DecimalConversionIcon';
import { useCreateFractionalLeave } from '@/hooks/absence/useCreateFractionalLeave';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { colors } from '@/assets/styles/colors';


export interface CreateFractionalLeaveFormProps {
  /** Colaborador seleccionado en el selector compartido del panel. */
  employeeTargetId: number | null;
}

export const CreateFractionalLeaveForm: React.FC<CreateFractionalLeaveFormProps> = ({
  employeeTargetId,
}) => {
  const { t: translate } = useTranslation('absence');

  const {
    form,
    isSubmitted,
    isSubmitting,
    handleSubmit,
    fractionalLeaveTypeOptions,
    isLoadingFractionalLeaveTypes,
    calculatedLateHoursDecimalDisplay,
    calculatedLateHoursTimeDisplay,
    isLoadingCalculatedLateHours,
    hoursToJustifyDecimalValue,
    handleHoursToJustifyDecimalChange,
    handleHoursToJustifyDecimalBlur,
    handleHoursToJustifyTimeChange,
    handleHoursToJustifyTimeBlur,
  } = useCreateFractionalLeave({ employeeTargetId });

  const calculatedLateHoursLoadingLabel = translate('fractionalLeave.form.calculatedLateHoursLoading');
  const calculatedLateHoursDecimalValue = isLoadingCalculatedLateHours
    ? calculatedLateHoursLoadingLabel
    : calculatedLateHoursDecimalDisplay;
  const calculatedLateHoursTimeValue = isLoadingCalculatedLateHours
    ? calculatedLateHoursLoadingLabel
    : calculatedLateHoursTimeDisplay;

  // Estos campos viven dentro del <form> de CreateSpecialLeaveForm (onSubmit ligado a
  // useCreateSpecialLeave). Sin esto, Enter en un input de texto dispara el submit nativo
  // de ESE formulario en vez de este handleSubmit, validando el esquema equivocado en
  // silencio. className="contents" evita alterar el layout de grid/flex del padre.
  const preventNativeFormSubmitOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'TEXTAREA') {
      event.preventDefault();
    }
  };

  return (
    <div className="contents" onKeyDown={preventNativeFormSubmitOnEnter}>
      <SidePanelFormLayout.Fields>
        <Selector
          label={translate('fractionalLeave.form.fractionalLeaveType')}
          placeholder={translate('fractionalLeave.form.fractionalLeaveTypePlaceholder')}
          options={fractionalLeaveTypeOptions}
          value={form.watch('fractionalLeaveTypeId') ? String(form.watch('fractionalLeaveTypeId')) : undefined}
          onChange={(value) => {
            const numericValue = normalizeNumericSelectValue(value);
            if (numericValue !== undefined) {
              form.setValue('fractionalLeaveTypeId', numericValue, { shouldDirty: true });
            }
          }}
          disabled={isSubmitting || isLoadingFractionalLeaveTypes}
          color={form.errors.fractionalLeaveTypeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            form.errors.fractionalLeaveTypeId && isSubmitted
              ? translate(form.errors.fractionalLeaveTypeId.message as string)
              : undefined
          }
          required
          data-testid="fractional-leave-type-selector"
        />

        <DatePicker
          label={translate('fractionalLeave.form.startDate')}
          value={form.watch('startDate') || undefined}
          onChange={(date) => form.setValue('startDate', date, { shouldDirty: true })}
          disabled={isSubmitting}
          iconColor={colors.feedback.success300}
          color={form.errors.startDate && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.startDate && isSubmitted
              ? translate(form.errors.startDate.message as string)
              : undefined
          }
          required
          data-testid="fractional-leave-start-date-picker"
        />

        <div className="flex flex-col gap-1">
          <div className="flex items-center">
            <Text variant="small-regular" color={colors.grays.neutral66}>
              {translate('fractionalLeave.form.calculatedLateHours')}
            </Text>
            <InfoBubble
              text={translate('fractionalLeave.form.calculatedLateHoursTooltip')}
              data-testid="fractional-leave-calculated-late-hours-tooltip"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <LabelInput
              value={calculatedLateHoursDecimalValue}
              disabled
              readOnly
              iconRight={<DecimalConversionIcon color={colors.important.main} />}
              aria-label={translate('fractionalLeave.form.calculatedLateHoursDecimalLabel')}
              data-testid="fractional-leave-calculated-late-hours-decimal-input"
            />
            <LabelInput
              value={calculatedLateHoursTimeValue}
              disabled
              readOnly
              iconRight={<IconScout name="clockThree" size="sm" color={colors.important.main} />}
              aria-label={translate('fractionalLeave.form.calculatedLateHoursTimeLabel')}
              data-testid="fractional-leave-calculated-late-hours-time-input"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Text variant="small-regular" color={colors.grays.neutral33}>
            {translate('fractionalLeave.form.hoursToJustify')}
            <span className="ml-1">*</span>
          </Text>
          <div className="grid grid-cols-2 gap-4">
            <LabelInput
              placeholder={translate('fractionalLeave.form.hoursToJustifyDecimalPlaceholder')}
              value={hoursToJustifyDecimalValue}
              onChange={handleHoursToJustifyDecimalChange}
              onBlur={handleHoursToJustifyDecimalBlur}
              disabled={isSubmitting}
              iconRight={<DecimalConversionIcon color={colors.feedback.success300} />}
              color={form.errors.hoursToJustify ? 'error' : undefined}
              aria-label={translate('fractionalLeave.form.hoursToJustifyDecimalLabel')}
              data-testid="fractional-leave-hours-to-justify-decimal-input"
            />
            <LabelInput
              placeholder={translate('fractionalLeave.form.hoursToJustifyTimePlaceholder')}
              value={form.watch('hoursToJustify')}
              onChange={handleHoursToJustifyTimeChange}
              iconRight={<IconScout name="clockThree" size="sm" color={colors.feedback.success300} />}
              onBlur={handleHoursToJustifyTimeBlur}
              disabled={isSubmitting}
              color={form.errors.hoursToJustify ? 'error' : undefined}
              aria-label={translate('fractionalLeave.form.hoursToJustifyTimeLabel')}
              data-testid="fractional-leave-hours-to-justify-time-input"
            />
          </div>
          {form.errors.hoursToJustify && (
            <Text variant="small-regular" color={colors.feedback.error300}>
              {translate(form.errors.hoursToJustify.message as string)}
            </Text>
          )}
        </div>

        <TextArea
          label={translate('fractionalLeave.form.detail')}
          placeholder={translate('fractionalLeave.form.detailPlaceholder')}
          registration={form.register('detail')}
          disabled={isSubmitting}
          maxLength={100}
          showCharacterCount
          supportingText={
            form.errors.detail && isSubmitted
              ? translate(form.errors.detail.message as string)
              : undefined
          }
          data-testid="fractional-leave-detail-textarea"
        />
      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action="create">
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            fullWidth
            isLoading={isSubmitting}
            disabled={isSubmitting}
            data-testid="submit-fractional-leave-button"
          >
            {translate('fractionalLeave.form.submit')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </div>
  );
};

export default CreateFractionalLeaveForm;
